import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState, useSyncExternalStore } from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'

import { AllFiltersArea, TabFiltersProps } from '../filter.types'
import { formatArrayParamsWithBrackets, normalizeFiltersToObject } from '@lib/transformFiltersToParams'

export const FILTER_METADATA_KEY = 'filterMetadata'

const getFilterMetadataQueryKey = (filtersArea: AllFiltersArea, scopeId?: number, params?: unknown) =>
  [FILTER_METADATA_KEY, filtersArea, scopeId ?? null, params] as const

const getFormParams = <T extends FieldValues, Q>(form: UseFormReturn<T>) =>
  formatArrayParamsWithBrackets(normalizeFiltersToObject(form.getValues())) as Q

export const useFilterMetadata = <T extends FieldValues, Q>(
  filtersConfig: TabFiltersProps<T, Q>['filtersConfig'],
  form: UseFormReturn<T>,
) => {
  const { loadOptions, filtersArea, scopeId } = filtersConfig
  const queryClient = useQueryClient()
  const [params, setParams] = useState(() => getFormParams<T, Q>(form))

  const { data: filterData, isFetching: isLoading } = useQuery({
    queryKey: getFilterMetadataQueryKey(filtersArea, scopeId, params),
    queryFn: () => loadOptions(params),
    // Keeps options mounted while the next filter combination loads.
    placeholderData: keepPreviousData,
  })

  const loadFilterMetadata = useCallback(() => {
    const nextParams = getFormParams<T, Q>(form)

    // Applying filters must refresh the count even when this combination is already cached.
    void queryClient.invalidateQueries({
      queryKey: getFilterMetadataQueryKey(filtersArea, scopeId, nextParams),
      exact: true,
    })
    setParams(nextParams)
  }, [form, filtersArea, scopeId, queryClient])

  return { filterData, loadFilterMetadata, isLoading }
}

// Total result count of the filters currently applied in an area, read from the loaded metadata queries.
// Only entries of the same scope count (unscoped for global tables), so one scope's total never leaks into another.
export const useFilterCount = (filtersArea?: AllFiltersArea, scopeId?: number) => {
  const queryCache = useQueryClient().getQueryCache()
  const subscribe = useCallback((onChange: () => void) => queryCache.subscribe(onChange), [queryCache])

  const getCount = () => {
    if (!filtersArea) return 0

    const [current] = queryCache
      .findAll({ queryKey: [FILTER_METADATA_KEY, filtersArea, scopeId ?? null] })
      .filter((query) => query.state.data)
      // Mounted queries belong to the applied filters; otherwise the latest load wins.
      .sort(
        (a, b) =>
          Number(b.getObserversCount() > 0) - Number(a.getObserversCount() > 0) ||
          b.state.dataUpdatedAt - a.state.dataUpdatedAt,
      )

    return (current?.state.data as { count?: number } | undefined)?.count
  }

  return useSyncExternalStore(subscribe, getCount, getCount)
}
